/**
 * One sim clock for slot pickers and the console clock cards (tv-2na.10.3 /
 * tv-2na.10.7). Pure helpers: no store reads, no Date.now.
 *
 * Studio-local times are Melbourne wall-clock strings with their real offset
 * (`+10:00`, or `+11:00` in daylight saving; 7fra): readers slice the string for
 * the hour / day key, so a UTC `Z` string would read 03:00 for 13:00, and the old
 * fixed `+10:00` read an hour out from October to April.
 */
import { studioDayKey, studioLocalIso, studioWallTimeToIso } from '@atink/domain';

const QUARTER_MS = 15 * 60 * 1000;

/** `ms` as a studio-local `YYYY-MM-DDTHH:MM:SS+HH:MM` string. */
function toStudioIso(ms: number): string {
  return studioLocalIso(ms);
}

/**
 * The console clock cards ("Advance to EOD", "now-line 2pm", "in-chair
 * checkout") carry a time on the seed day. Decided 23 Sep: they apply their
 * TIME OF DAY to the CURRENT sim day, so after a day step they still move the
 * clock within the day being demoed. The result may be earlier than `clockIso`.
 */
export function timeOnSimDay(clockIso: string, cardIso: string): string {
  const day = studioDayKey(clockIso);
  const time = toStudioIso(Date.parse(cardIso)).slice(11, 19);
  return toStudioIso(Date.parse(studioWallTimeToIso(day, time)));
}

/** `iso` rounded UP to the next quarter hour (unchanged when already on one). */
export function ceilToQuarterHour(iso: string): string {
  const ms = Date.parse(iso);
  return toStudioIso(Math.ceil(ms / QUARTER_MS) * QUARTER_MS);
}

/**
 * A booking start can't be in the past. A chosen start that is still ahead of
 * the sim clock is kept as-is; a stale one (the clock moved on while the form
 * was open, or a window that began before now) moves to the next quarter hour
 * after now. Decided default (23 Sep).
 */
export function startNoEarlierThanNow(startIso: string, nowIso: string): string {
  if (Date.parse(startIso) >= Date.parse(nowIso)) return startIso;
  return ceilToQuarterHour(nowIso);
}
